// registryKeys.ts — typed keys for values stashed on the Phaser game registry, plus the boot-time
// URL-param parser. main.ts writes these once; scenes read them.
import type { Scene } from 'phaser';

export const REG = {
  seed: 'seed',
  topic: 'topic', // topicNormalized slug | '' (random / first)
  ai: 'ai', // boolean
  autoPlay: 'autoPlay', // ?play=1 — skip menu straight into a board
  debug: 'debug', // ?debug=1 — fps + dev hooks
  boardIndex: 'boardIndex', // which difficulty rung (drives difficultyForBoard)
  daily: 'daily', // ?daily=1 — a shared/deep-linked Daily run (counts toward the daily streak)
  dailyMutator: 'dailyMutator', // &m=N — the twist the DAILY LINK was minted under (-1 = resolve by date)
  challengeTarget: 'challengeTarget', // ?target=N — a friend's score to beat on this exact board (P1.2)
  challengeGhost: 'challengeGhost', // ?ghost=<compact> — a friend's route to race (P1.2b)
  ghostSource: 'ghostSource', // GHOST DUELS: 'last-daily' → race the LOCAL saved daily ghost (Slice A, no net)
  ghostRemoteId: 'ghostRemoteId', // GHOST DUELS Slice B: &gr=<id> → fetch + race a friend's KV-stored ghost
  // P1.2c best-of-three series state (deep link &series=bo3 &g=<game> &y=<youWins> &t=<themWins>):
  series: 'series', // SeriesMode string | '' (no series)
  seriesGame: 'seriesGame', // 1-based current game
  seriesYou: 'seriesYou', // local-player series wins
  seriesThem: 'seriesThem', // friend series wins
} as const;

export interface BootParams {
  seed: number;
  topic: string;
  ai: boolean;
  autoPlay: boolean;
  debug: boolean;
  /** ?daily=1 — the run is (or was deep-linked as) a Daily; drives the daily-streak credit. */
  daily: boolean;
  /** &m=N — the daily mutator the link was minted under, so a cross-day open replays the SAME twist
   *  (layout + rules) the sharer's target/ghost were set on. -1 = none: resolve today's by date. */
  dailyMutator: number;
  /** ?target=N — P1.2 challenge: a friend's score to beat on this seed+topic. 0 = no challenge. */
  challengeTarget: number;
  /** ?ghost=<compact> — P1.2b: a friend's route to race ('' = none). */
  challengeGhost: string;
  /** GHOST DUELS Slice A: 'last-daily' races the LOCAL saved daily ghost (trivia.lastGhost.v1); '' = none.
   *  Inline, offline-only — this is NOT a network fetch (the &gr KV path is Slice B). */
  ghostSource: 'last-daily' | '';
  /** GHOST DUELS Slice B: &gr=<id> — a friend's KV-stored ghost to fetch + race ('' = none). Sanitized to
   *  [A-Za-z0-9-]{1,64}; the bv gate happens PER BLOB after fetch (the blob carries its own bv), not here. */
  ghostRemoteId: string;
  /** P1.2c best-of-three: the raw &series= value ('bo3' enables it, '' = none). */
  seriesMode: string;
  /** P1.2c: raw &g= current game number (default 1). */
  seriesGame: number;
  /** P1.2c: raw &y= local-player series wins (default 0). */
  seriesYou: number;
  /** P1.2c: raw &t= friend series wins (default 0). */
  seriesThem: number;
}

/** Board-layout balance version — bump when difficulty constants change board generation OR the sim
 *  outcome a ghost was recorded against.
 *  v3 (2026-07-02): rung-0 opener sheds a row (5×5 phone) — v2 ghosts mint against 5×6 rung-0 layouts.
 *  v4 (2026-07-02): daily mutators — Daily layout is now a function of the day's twist, so v3 daily
 *  ghosts (pre-mutator layouts) must decode-drop.
 *  v5 (2026-07-03): owner-feedback slice — safe-ground skip + CHOMP SURGE purge + hazard-cadence soften
 *  change the sim outcome (and the Daily's base cadence 9000→10000), so v4 ghosts replay against a
 *  different run and must decode-drop by design.
 *  v6 (2026-07-03, FACE&FURY S1 CORE-THREAD): gauntletArmThreshold 2→1 makes threading the default path
 *  (blank-slate median 0→2.0 threads/run) — the ×1.5 now fires on far more corrects, changing every
 *  player's AND the rival's score trajectory — plus the early-ramp fenceRatio floor raise (rungs 0-1)
 *  shifts board layouts. v5 ghosts replay against a different sim outcome and must decode-drop by design
 *  (fail-closed friendly rejection). The Daily base profile is unchanged (its fence stays ≤0.3 for
 *  cold-player fairness — growth-smoke guards that), but its layout still re-keys under the new bv.
 *  v7 (2026-07-04, FACE&FURY V2-R1 RIVAL PRESENCE): the mid/late AI tiers were retuned so boards 3-5
 *  stay contested (sharp reactionDelay 620→380 / acc 0.72→0.80 / risk 0.55→0.68 / grace 1400→1000;
 *  savant 300→245 / 0.84→0.87 / 0.8→0.85 — see src/core/ai.ts). These constants change the same-seed
 *  OUTCOME of any run touching rungs 2-4 (the rival + human score trajectories diverge), so a v6 ghost
 *  replays against a different sim and must decode-drop by design. This is the ONE v2-arc bv bump
 *  (director-ratified, mirrors the v1 arc's single bump) — later v2 slices must NOT re-bump. NOTE: the
 *  ghost-minting boards themselves (board 0 = rung 0 rookie, and the Daily = rookie) use the UNTOUCHED
 *  rookie tier, so a v6 board-0/daily ghost would in fact replay identically — the bump is the
 *  conservative arc-wide balance marker, not a claim that every minting board shifted. */
export const GHOST_BALANCE_VERSION = '7';

export function parseBootParams(search: string): BootParams {
  const q = new URLSearchParams(search);
  const seedRaw = q.get('seed');
  const seed = seedRaw !== null && Number.isFinite(Number(seedRaw)) ? Number(seedRaw) : 11;
  const topic = q.get('topic') ?? '';
  const aiRaw = q.get('ai');
  const ai = aiRaw === null ? true : aiRaw !== '0' && aiRaw.toLowerCase() !== 'false';
  const autoPlay = q.get('play') === '1';
  const debug = q.get('debug') === '1';
  const daily = q.get('daily') === '1';
  const mRaw = q.get('m');
  const dailyMutator = daily && mRaw !== null && Number.isFinite(Number(mRaw)) ? Math.floor(Number(mRaw)) : -1;
  const targetRaw = q.get('target');
  const challengeTarget =
    targetRaw !== null && Number.isFinite(Number(targetRaw)) ? Math.max(0, Math.floor(Number(targetRaw))) : 0;
  // Ghost routes are only valid against the balance version that minted them (board layout is a
  // function of seed + difficulty constants; the 2026-07-01 fence-floor tune is version 2). A stale
  // link still PLAYS (same seed/topic/target) — only the misaligned ghost overlay is dropped.
  const ghostRaw = q.get('ghost') ?? '';
  const challengeGhost = ghostRaw !== '' && q.get('bv') === GHOST_BALANCE_VERSION ? ghostRaw : '';
  // GHOST DUELS Slice A self-race (offline, local): ?raceghost=1 (or ?ghostSource=last-daily) races the
  // saved daily ghost. Only meaningful on a Daily; GameScene gates it on isDaily + the bv/seed/m/topic pin.
  const ghostSource: BootParams['ghostSource'] =
    q.get('raceghost') === '1' || q.get('ghostSource') === 'last-daily' ? 'last-daily' : '';
  // GHOST DUELS Slice B remote ghost: &gr=<id>. Sanitize to the KV-id charset; the blob's OWN bv gates the
  // replay after fetch (a stale-layout ghost decode-drops), so — unlike &ghost — there is no parse-time bv.
  const grRaw = q.get('gr') ?? '';
  const ghostRemoteId = /^[A-Za-z0-9-]{1,64}$/.test(grRaw) ? grRaw : '';
  // P1.2c best-of-three series params (validated/clamped downstream by series.parseSeries).
  const seriesMode = (q.get('series') ?? '').toLowerCase();
  const intOr = (raw: string | null, def: number): number =>
    raw !== null && Number.isFinite(Number(raw)) ? Math.floor(Number(raw)) : def;
  const seriesGame = intOr(q.get('g'), 1);
  const seriesYou = intOr(q.get('y'), 0);
  const seriesThem = intOr(q.get('t'), 0);
  return {
    seed, topic, ai, autoPlay, debug, daily, dailyMutator, challengeTarget, challengeGhost, ghostSource,
    ghostRemoteId, seriesMode, seriesGame, seriesYou, seriesThem,
  };
}

/** Convenience reader so scenes don't repeat registry.get casts. */
export function readBoot(scene: Scene): BootParams {
  const r = scene.registry;
  return {
    seed: (r.get(REG.seed) as number) ?? 11,
    topic: (r.get(REG.topic) as string) ?? '',
    ai: (r.get(REG.ai) as boolean) ?? true,
    autoPlay: (r.get(REG.autoPlay) as boolean) ?? false,
    debug: (r.get(REG.debug) as boolean) ?? false,
    daily: (r.get(REG.daily) as boolean) ?? false,
    dailyMutator: (r.get(REG.dailyMutator) as number) ?? -1,
    challengeTarget: (r.get(REG.challengeTarget) as number) ?? 0,
    challengeGhost: (r.get(REG.challengeGhost) as string) ?? '',
    ghostSource: (r.get(REG.ghostSource) as BootParams['ghostSource']) ?? '',
    ghostRemoteId: (r.get(REG.ghostRemoteId) as string) ?? '',
    seriesMode: (r.get(REG.series) as string) ?? '',
    seriesGame: (r.get(REG.seriesGame) as number) ?? 1,
    seriesYou: (r.get(REG.seriesYou) as number) ?? 0,
    seriesThem: (r.get(REG.seriesThem) as number) ?? 0,
  };
}
